// owner: social-graph domain agent
import { z } from 'zod';
import { EpochMsSchema, PaginationQuerySchema, UlidSchema, paginated } from './common';
import { PersonRefSchema } from './network';

export const MessageBodySchema = z.string().trim().min(1).max(5000);
/** Max messages one agent token may send per minute (fixed window, KV counter). */
export const AGENT_MESSAGES_PER_MINUTE = 10;

export const ThreadCreateSchema = z
  .object({
    recipientId: UlidSchema,
    subject: z.string().trim().max(200).default(''),
    body: MessageBodySchema,
    contextKind: z.enum(['application']).nullish(),
    contextId: UlidSchema.nullish(),
    /** An attachment already uploaded to the thread being continued. */
    attachmentId: UlidSchema.optional(),
  })
  .refine((v) => Boolean(v.contextKind) === Boolean(v.contextId), {
    path: ['contextId'],
    message: 'contextKind and contextId must be provided together',
  });
export const MessageSendSchema = z.object({
  body: MessageBodySchema,
  /** An attachment uploaded via POST /threads/:id/attachments, not yet attached to a message. */
  attachmentId: UlidSchema.optional(),
});

export const MessageAttachmentSchema = z.object({
  id: UlidSchema,
  contentType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
});
export const AttachmentUploadSchema = MessageAttachmentSchema.extend({
  attachmentId: UlidSchema,
}).omit({ id: true });

export const MessageSchema = z.object({
  id: UlidSchema,
  threadId: UlidSchema,
  senderId: UlidSchema,
  body: z.string(),
  /** True when an agent token (not the human) sent it. Surfaced in the UI as a label. */
  viaAgent: z.boolean(),
  /** True when every other participant has read up to this message. */
  readByOthers: z.boolean(),
  attachment: MessageAttachmentSchema.nullable(),
  createdAt: EpochMsSchema,
});
export type Message = z.infer<typeof MessageSchema>;
export const MessageListSchema = paginated(MessageSchema);
export const MessageListQuerySchema = PaginationQuerySchema;

/**
 * null = normal thread. A message request from a non-connection starts 'pending'; the recipient
 * replying auto-accepts it ('accepted'), or may explicitly 'ignore' it (blocks further sends from
 * the requester). See GET/POST /requests.
 */
export const RequestStatusSchema = z.enum(['pending', 'accepted', 'ignored']);
export type RequestStatus = z.infer<typeof RequestStatusSchema>;

export const ThreadSchema = z.object({
  id: UlidSchema,
  subject: z.string(),
  contextKind: z.string().nullable(),
  contextId: z.string().nullable(),
  requestStatus: RequestStatusSchema.nullable(),
  participants: z.array(PersonRefSchema.extend({ lastReadAt: EpochMsSchema })),
  lastMessage: z
    .object({
      body: z.string(),
      senderId: UlidSchema,
      viaAgent: z.boolean(),
      createdAt: EpochMsSchema,
    })
    .nullable(),
  unreadCount: z.number().int(),
  lastMessageAt: EpochMsSchema,
  createdAt: EpochMsSchema,
});
export type Thread = z.infer<typeof ThreadSchema>;
export const ThreadListSchema = paginated(ThreadSchema);
export const ThreadListQuerySchema = PaginationQuerySchema;
export const ThreadIdParamsSchema = z.object({ id: UlidSchema });
export const ThreadCreatedSchema = z.object({ thread: ThreadSchema, message: MessageSchema });
export const ReadReceiptSchema = z.object({ threadId: UlidSchema, lastReadAt: EpochMsSchema });

export const NotificationSchema = z.object({
  id: UlidSchema,
  kind: z.string(),
  title: z.string(),
  body: z.string(),
  link: z.string().nullable(),
  entityKind: z.string().nullable(),
  entityId: z.string().nullable(),
  readAt: EpochMsSchema.nullable(),
  createdAt: EpochMsSchema,
});
export const NotificationListQuerySchema = PaginationQuerySchema.extend({
  unread: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
});
export const NotificationListSchema = z.object({
  items: z.array(NotificationSchema),
  nextCursor: z.string().nullable(),
  unreadCount: z.number().int(),
});
export const NotificationsMarkReadSchema = z
  .object({ ids: z.array(UlidSchema).min(1).max(100).optional(), all: z.literal(true).optional() })
  .refine((v) => Boolean(v.ids) !== Boolean(v.all), 'Provide exactly one of ids or all:true');
export const MarkedReadSchema = z.object({ updated: z.number().int() });

// ------------------------------------------------------------ notification preferences

/**
 * Known `notifications.kind` values (grepped from `kind: '...'` literals across the routes, plus
 * `job_alert` for saved job searches and a handful of future-proofing kinds). This is only used to
 * validate the preferences a user may explicitly set; `notify()` itself accepts any kind string, and
 * the preference check for an unrecognized kind defaults to "send" (missing row = both on).
 */
export const NOTIFICATION_TYPES = [
  'connection.request',
  'connection.accept',
  'follow.new',
  'message.new',
  'comment.new',
  'post.reaction',
  'comment.reaction',
  'job_alert',
  'endorsement',
  'recommendation',
  'mention',
  'invite',
] as const;
export const NotificationTypeSchema = z.enum(NOTIFICATION_TYPES);
export type NotificationType = z.infer<typeof NotificationTypeSchema>;

export const NotificationPreferenceSchema = z.object({
  type: NotificationTypeSchema,
  inApp: z.boolean(),
  email: z.boolean(),
});
export const NotificationPreferencesListSchema = z.object({
  items: z.array(NotificationPreferenceSchema),
});
export const NotificationPreferencesUpdateSchema = z
  .array(z.object({ type: NotificationTypeSchema, inApp: z.boolean(), email: z.boolean() }))
  .min(1)
  .max(NOTIFICATION_TYPES.length);
